function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import produce from 'immer';
import { useTranslation } from 'common/i18n/hooks';
import { SvgIconReact } from 'common/components/svg-icon';
import { prompt } from 'common/components/confirm';
import { MetricEntryBehavior } from 'common/query-builder/query-entry-section/types';
import { MetricType } from 'common/types/reports/bookmark';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetricEntries } from 'common/report/insights/contexts/metrics';
import { SectionItem } from 'common/components/items-menu/types';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { BehaviorTypeIcons } from 'common/report/insights/util/metrics';
import { OverflowControls } from '../common/controls';
import { checkUpsellForMetricBehavior } from '../utils';
import { BehaviorSelector } from '../common/behavior-selector';
import styles from './index.module.styl';
import type { RowProps } from './catalog';
type MetricBehaviorProps = RowProps & {
  metricType: MetricType;
  children?: React.ReactNode;
  behavior?: MetricEntryBehavior;
};

/**
 * Defines the wrapping component for a behavior definition.
 * This component is responsible for:
 * - Saving a behavior
 * - Reset changes on a changed behavior
 * - Selecting a saved behavior
 */
export function MetricBehaviorBase(props: MetricBehaviorProps) {
  var _useCurrentProject;
  const {
    t
  } = useTranslation();
  const behaviors = useBehaviors();
  const currentProject = (_useCurrentProject = useCurrentProject()) === null || _useCurrentProject === void 0 || (_useCurrentProject = _useCurrentProject.get()) === null || _useCurrentProject === void 0 ? void 0 : _useCurrentProject.value;
  const checkUpsell = checkUpsellForMetricBehavior(currentProject);
  const {
    saveBehavior,
    resetBehavior,
    getSaveBehaviorDisabledMessage
  } = useMetricEntries();
  const [isSaving, setIsSaving] = React.useState(false);
  const behavior = props.entry.behavior;
  const hasUnsavedChanges = behavior.id ? behavior.hasUnsavedChanges : false;
  const updateBehavior = (updates: Partial<MetricEntryBehavior>) => {
    props.updateEntry(produce(props.entry, draft => {
      Object.assign(draft.behavior, updates);
    }));
  };
  const updateDisabledMessage = getSaveBehaviorDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex
  });
  const handleSaveBehavior = async function handleSaveBehavior() {
    let saveAsNew = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (checkUpsell.shouldUpsell) {
      return checkUpsell.upsellAction();
    }
    setIsSaving(true);
    await saveBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew
    });
    setIsSaving(false);
  };
  const handleResetBehavior = () => {
    resetBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex
    });
  };
  const handleRenameBehavior: () => void = async () => {
    const promptResponse = await prompt({
      /** @todo(metrics) plug in automatic naming here */
      'initial-input-value': behavior.name || t("query-builder.metrics.new-definition"),
      'allow-empty-input-value': false,
      'show-input': true
    }, {
      inputValidator(stagedInput) {
        const trimmed = stagedInput.trim();
        const labelIsUnchanged = trimmed === behavior.name;
        const isUnique = behaviors.getAllValues().filter(value => value.id !== behavior.id).every(value => value.name !== trimmed);
        return labelIsUnchanged || isUnique ? null : t("components.create-entity.errors.name-exists");
      }
    }, {
      title: _ref => {
        let {
          t
        } = _ref;
        return t("query-builder.actions.rename");
      },
      confirmButtonLabel: _ref2 => {
        let {
          t
        } = _ref2;
        return t("common.done");
      }
    });
    if (promptResponse.didConfirm) {
      updateBehavior({
        name: promptResponse.inputValue.trim()
      });
    }
  };
  return <div className={clsx(styles.behavior, styles[camelCase("is-".concat(props.metricType, "-metric"))])} data-sentry-component="MetricBehaviorBase" data-sentry-source-file="base.tsx">
      {/* metric header area */}
      <div className={styles.header}>
        {/* behavior label/selector */}
        <SvgIconReact className={clsx(styles.icon, styles.indicator, {
        [styles.hasUnsavedChanges]: hasUnsavedChanges
      })} icon={BehaviorTypeIcons[props.metricType as keyof typeof BehaviorTypeIcons]} data-sentry-element="SvgIconReact" data-sentry-source-file="base.tsx" />

        <div className={styles.content}>
          <BehaviorSelector {...props} data-sentry-element="BehaviorSelector" data-sentry-source-file="base.tsx" />
        </div>

        {/* indicate sync status  */}
        {isSaving && <span className={styles.status}>{t("common.saving_suspense")}</span>}

        {/* metric controls */}
        <div className={styles.actions}>
          {/* metric overflow controls for saving, resetting, etc... */}
          <OverflowControls items={[{
          items: [
          // show "Update Behavior" only when saved and with local changes
          hasUnsavedChanges && {
            label: t("query-builder.behaviors.update"),
            icon: "push-up",
            onSelect: () => handleSaveBehavior(),
            isDisabled: Boolean(updateDisabledMessage),
            tooltip: updateDisabledMessage ? {
              text: updateDisabledMessage
            } : null
          },
          // show "Save Behavior" only when unsaved
          !behavior.id && _objectSpread({
            label: t("query-builder.behaviors.save"),
            icon: "save",
            onSelect: () => handleSaveBehavior()
          }, checkUpsell.shouldUpsell && {
            icon: "brand-upgrade"
          }),
          // show "Save as New Behavior" only when rendering a saved behavior
          behavior.id && _objectSpread({
            label: t("query-builder.behaviors.save-as-new"),
            icon: "save",
            onSelect: () => handleSaveBehavior(true)
          }, checkUpsell.shouldUpsell && {
            icon: "brand-upgrade"
          }),
          // show "Reset" only when dealing with a saved behavior with local changes
          hasUnsavedChanges && {
            label: t("query-builder.behaviors.reset"),
            icon: "undo",
            onSelect: handleResetBehavior
          }, {
            label: t("query-builder.actions.rename"),
            icon: "edit",
            onSelect: handleRenameBehavior
          }].filter(Boolean) as SectionItem[]
        }]} data-sentry-element="OverflowControls" data-sentry-source-file="base.tsx" />
        </div>
      </div>

      {/* behavior definition */}
      <div className={styles.content}>{props.children}</div>
    </div>;
}